import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ParkingLot } from './models/ParkingLot';
import { SimulationManager } from './models/SimulationManager';
import { Car } from './models/Car';
import { Person } from './models/Person';
import ParkingLotComponent from './components/ParkingLot';
import SchedulePanel from './components/SchedulePanel';
import CompletedCarsPanel from './components/CompletedCarsPanel';
import About from './components/About';
import './App.css';

// Shared schedule generator to ensure both simulations get identical schedules
const generateSharedSchedule = (numCars = 80) => {
  const maxArrivalTime = 45 * 60; // 45 minutes in seconds
  const scheduledCars = [];
  
  console.log(`🚗 Generating SHARED schedule for ${numCars} cars...`);
  console.log(`⏰ Cars will arrive between 00:00 and ${formatArrivalTime(maxArrivalTime)}`);
  
  for (let i = 0; i < numCars; i++) {
    // Ensure the first car arrives at t=0
    const arrivalTime = i === 0 ? 0 : Math.floor(Math.random() * maxArrivalTime);
    const car = new Car(arrivalTime);
    const person = new Person();
    
    // Associate car and person
    person.setCar(car);
    
    scheduledCars.push({ car, person });
  }
  
  // Sort scheduled cars by arrival time
  scheduledCars.sort((a, b) => a.car.getArrivalTime() - b.car.getArrivalTime());
  
  // Log the complete schedule
  console.log('📅 Complete SHARED Car Schedule:');
  console.log('Time | Car ID | Unicode | Person Store Visit');
  console.log('-----|--------|---------|------------------');
  
  scheduledCars.forEach(({ car, person }, index) => {
    const arrivalTime = formatArrivalTime(car.getArrivalTime());
    const storeVisitMinutes = Math.floor(person.getStoreVisitTime() / 60);
    const storeVisitSeconds = person.getStoreVisitTime() % 60;
    console.log(
      `${arrivalTime} | ${car.id.substring(0, 6)}... | ${car.getUnicodeChar()} | ${storeVisitMinutes}m ${storeVisitSeconds}s`
    );
  });
  
  console.log(`✅ SHARED Schedule generated successfully! ${scheduledCars.length} cars scheduled.`);
  console.log('First 5 cars to arrive:', scheduledCars.slice(0, 5).map(({ car }) => ({
    time: formatArrivalTime(car.getArrivalTime()),
    unicode: car.getUnicodeChar(),
    id: car.id.substring(0, 6)
  })));
  
  return scheduledCars;
};

// Function to clone a Person object with all its properties
const clonePerson = (originalPerson) => {
  const clonedPerson = new Person();
  
  // Copy all the properties from the original person
  clonedPerson.id = originalPerson.id;
  clonedPerson.handicapped = originalPerson.handicapped;
  clonedPerson.walkSpeed = originalPerson.walkSpeed;
  clonedPerson.lotSpeed = originalPerson.lotSpeed;
  clonedPerson.storeVisitTime = originalPerson.storeVisitTime;
  clonedPerson.isInStore = originalPerson.isInStore;
  clonedPerson.isWalking = originalPerson.isWalking;
  clonedPerson.storeEntryTime = originalPerson.storeEntryTime;
  clonedPerson.storeExitTime = originalPerson.storeExitTime;
  clonedPerson.shoppingCompleteTime = originalPerson.shoppingCompleteTime;
  clonedPerson.hasCompletedShopping = originalPerson.hasCompletedShopping;
  clonedPerson.totalDrivingTime = originalPerson.totalDrivingTime;
  clonedPerson.totalAccumulatedWalkTime = originalPerson.totalAccumulatedWalkTime;
  clonedPerson.x = originalPerson.x;
  clonedPerson.y = originalPerson.y;
  clonedPerson.startX = originalPerson.startX;
  clonedPerson.startY = originalPerson.startY;
  clonedPerson.targetX = originalPerson.targetX;
  clonedPerson.targetY = originalPerson.targetY;
  clonedPerson.walkStartTime = originalPerson.walkStartTime;
  clonedPerson.totalWalkTime = originalPerson.totalWalkTime;
  clonedPerson.isWalkingToStore = originalPerson.isWalkingToStore;
  clonedPerson.isWalkingToCar = originalPerson.isWalkingToCar;
  clonedPerson.walkCompleteTime = originalPerson.walkCompleteTime;
  
  return clonedPerson;
};

// Function to clone a Car object with all its properties
const cloneCar = (originalCar) => {
  const clonedCar = new Car(originalCar.getArrivalTime());
  
  // Copy all the properties from the original car
  clonedCar.id = originalCar.id;
  clonedCar.color = originalCar.color;
  clonedCar.unicodeChar = originalCar.unicodeChar;
  clonedCar.status = originalCar.status;
  clonedCar.arrivalTime = originalCar.arrivalTime;
  clonedCar.parkingTime = originalCar.parkingTime;
  clonedCar.exitTime = originalCar.exitTime;
  clonedCar.x = originalCar.x;
  clonedCar.y = originalCar.y;
  clonedCar.targetX = originalCar.targetX;
  clonedCar.targetY = originalCar.targetY;
  clonedCar.startX = originalCar.startX;
  clonedCar.startY = originalCar.startY;
  clonedCar.moveStartTime = originalCar.moveStartTime;
  clonedCar.moveDuration = originalCar.moveDuration;
  clonedCar.parkingLot = originalCar.parkingLot;
  clonedCar.parkingSpace = originalCar.parkingSpace;
  clonedCar.person = originalCar.person;
  
  return clonedCar;
};

const formatArrivalTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

function App() {
  // State for simulation controls
  const [handicappedWalkSpeedPenalty, setHandicappedWalkSpeedPenalty] = useState(1.3); // 30% slower by default
  const [numCars, setNumCars] = useState(80); // 80 cars by default
  
  // Calculate scaling factor based on screen width relative to 1000px
  const [scalingFactor, setScalingFactor] = useState(1);
  
  // Generate the shared schedule once with initial numCars
  const [sharedSchedule, setSharedSchedule] = useState(() => generateSharedSchedule(numCars));
  
  // Create two parking lots - one with handicapped spaces, one without
  const [parkingLotWithHandicapped] = useState(() => new ParkingLot(1000, 500));
  const [parkingLotWithoutHandicapped] = useState(() => {
    const lot = new ParkingLot(1000, 500);
    // Remove handicapped designation from all spaces
    lot.getSpaces().forEach(space => {
      space.handicapped = false;
    });
    return lot;
  });
  
  // Create two simulation managers with the SHARED schedule
  const [simulationManagerWithHandicapped] = useState(() => {
    const manager = new SimulationManager(parkingLotWithHandicapped);
    // Replace the generated schedule with our shared schedule - clone everything exactly
    manager.scheduledCars = sharedSchedule.map(({ car, person }) => {
      const clonedCar = cloneCar(car);
      const clonedPerson = clonePerson(person);
      
      // Set the handicapped walk speed penalty
      if (clonedPerson.handicapped) {
        clonedPerson.setWalkSpeedPenalty(handicappedWalkSpeedPenalty);
      }
      
      // Associate the cloned car and person
      clonedPerson.setCar(clonedCar);
      
      return { car: clonedCar, person: clonedPerson };
    });
    return manager;
  });
  
  const [simulationManagerWithoutHandicapped] = useState(() => {
    const manager = new SimulationManager(parkingLotWithoutHandicapped);
    // Replace the generated schedule with our shared schedule - clone everything exactly
    manager.scheduledCars = sharedSchedule.map(({ car, person }) => {
      const clonedCar = cloneCar(car);
      const clonedPerson = clonePerson(person);
      
      // Set the handicapped walk speed penalty
      if (clonedPerson.handicapped) {
        clonedPerson.setWalkSpeedPenalty(handicappedWalkSpeedPenalty);
      }
      
      // Associate the cloned car and person
      clonedPerson.setCar(clonedCar);
      
      return { car: clonedCar, person: clonedPerson };
    });
    return manager;
  });

  // Helper to restart simulation with new options
  const restartSimulationWithOptions = (newPenalty, newNumCars) => {
    // Update state
    setHandicappedWalkSpeedPenalty(newPenalty);
    setNumCars(newNumCars);
    // Regenerate schedule
    const newSchedule = generateSharedSchedule(newNumCars);
    setSharedSchedule(newSchedule);
    // FULL RESET: clear all cars, people, completedCars, and vacate all spaces in both lots
    [simulationManagerWithHandicapped, simulationManagerWithoutHandicapped].forEach(manager => {
      // Remove all cars from spaces
      manager.cars.forEach(car => car.leave && car.leave());
      // Clear arrays
      manager.cars = [];
      manager.people = [];
      manager.completedCars = [];
      // Vacate all spaces in the lot
      manager.parkingLot.getSpaces().forEach(space => {
        space.vacate && space.vacate(0);
        space.occupancyHistory = [];
        space.totalOccupiedTime = 0;
        space.occupancyCount = 0;
        space.currentOccupancyStart = null;
      });
    });
    // Update simulation managers with new schedule
    simulationManagerWithHandicapped.scheduledCars = newSchedule.map(({ car, person }) => {
      const clonedCar = cloneCar(car);
      const clonedPerson = clonePerson(person);
      if (clonedPerson.handicapped) {
        clonedPerson.setWalkSpeedPenalty(newPenalty);
      }
      clonedPerson.setCar(clonedCar);
      return { car: clonedCar, person: clonedPerson };
    });
    simulationManagerWithoutHandicapped.scheduledCars = newSchedule.map(({ car, person }) => {
      const clonedCar = cloneCar(car);
      const clonedPerson = clonePerson(person);
      if (clonedPerson.handicapped) {
        clonedPerson.setWalkSpeedPenalty(newPenalty);
      }
      clonedPerson.setCar(clonedCar);
      return { car: clonedCar, person: clonedPerson };
    });
    // Reset time and start simulation
    setTime(0);
    simulationManagerWithHandicapped.setCurrentTime(0);
    simulationManagerWithoutHandicapped.setCurrentTime(0);
    setIsSimulationRunning(true);
    simulationManagerWithHandicapped.startSimulation();
    simulationManagerWithoutHandicapped.startSimulation();
  };

  // State to track which simulation is visible
  const [isHandicappedMode, setIsHandicappedMode] = useState(true);
  
  // Use the visible simulation manager based on the mode
  const visibleSimulationManager = isHandicappedMode ? simulationManagerWithHandicapped : simulationManagerWithoutHandicapped;
  const visibleParkingLot = isHandicappedMode ? parkingLotWithHandicapped : parkingLotWithoutHandicapped;
  
  const [time, setTime] = useState(0);
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [hoveredCarInfo, setHoveredCarInfo] = useState(null);
  const [simulationSpeed, setSimulationSpeed] = useState(32); // Default 32x
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);

  // Total simulation time: 3600 seconds (1 hour)
  const TOTAL_SIMULATION_TIME = 3600;

  // Map speed to interval in ms
  const speedToInterval = {
    1: 1000,
    4: 250,
    8: 125,
    16: 62.5,
    32: 31.25,
  };

  useEffect(() => {
    // Start both simulations automatically on mount
    setTime(0);
    simulationManagerWithHandicapped.setCurrentTime(0);
    simulationManagerWithoutHandicapped.setCurrentTime(0);
    setIsSimulationRunning(true);
    simulationManagerWithHandicapped.startSimulation();
    simulationManagerWithoutHandicapped.startSimulation();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let interval;
    if (isSimulationRunning && time < TOTAL_SIMULATION_TIME) {
      interval = setInterval(() => {
        setTime((prev) => {
          const newTime = prev + 1;
          // Update both simulations in parallel
          simulationManagerWithHandicapped.setCurrentTime(newTime);
          simulationManagerWithoutHandicapped.setCurrentTime(newTime);
          
          if (newTime >= TOTAL_SIMULATION_TIME) {
            setIsSimulationRunning(false);
            simulationManagerWithHandicapped.pauseSimulation();
            simulationManagerWithoutHandicapped.pauseSimulation();
            return TOTAL_SIMULATION_TIME;
          }
          return newTime;
        });
      }, speedToInterval[simulationSpeed]);
    }
    return () => clearInterval(interval);
  }, [isSimulationRunning, time, simulationManagerWithHandicapped, simulationManagerWithoutHandicapped, simulationSpeed]);

  // Calculate scaling factor on page load and window resize
  useEffect(() => {
    const calculateScalingFactor = () => {
      // Try multiple methods to get the most accurate viewport width
      let screenWidth = window.innerWidth;
      
      // Method 1: Use visualViewport API if available (more reliable for mobile simulation)
      if (window.visualViewport) {
        screenWidth = window.visualViewport.width;
      }
      
      // Method 2: Try to get the actual container width from the DOM
      const container = document.querySelector('.parking-lot-container');
      if (container) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 0) {
          // Use the container's actual available width
          screenWidth = rect.width;
        }
      }
      
      const trueScreenWidth = screenWidth; // Account for padding
      const baseWidth = 1000;
      const factor = Math.min(trueScreenWidth / baseWidth, 1); // Don't scale up beyond 1.0
      setScalingFactor(factor);
      
      // Enhanced logging for debugging
      console.log(`📱 Scaling Factor Debug:`);
      console.log(`  - window.innerWidth: ${window.innerWidth}px`);
      console.log(`  - visualViewport.width: ${window.visualViewport ? window.visualViewport.width : 'N/A'}px`);
      console.log(`  - container width: ${container ? container.getBoundingClientRect().width : 'N/A'}px`);
      console.log(`  - final screenWidth: ${screenWidth}px`);
      console.log(`  - trueScreenWidth: ${trueScreenWidth}px`);
      console.log(`  - scaling factor: ${factor.toFixed(2)}`);
      console.log(`  - user agent: ${navigator.userAgent}`);
    };

    // Wait for the container to be available before setting up observers
    const setupObservers = () => {
      const container = document.querySelector('.parking-lot-container');
      if (!container) {
        // Container not ready yet, try again in a bit
        setTimeout(setupObservers, 100);
        return;
      }

      // Method 3: Use ResizeObserver to detect container size changes
      let resizeObserver;
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          // Debounce the calculation to avoid excessive updates
          clearTimeout(resizeObserver.timeout);
          resizeObserver.timeout = setTimeout(calculateScalingFactor, 100);
        });
        resizeObserver.observe(container);
      }

      // Store the observer for cleanup
      return resizeObserver;
    };

    // Calculate on mount
    calculateScalingFactor();
    
    // Also calculate after a short delay to ensure DOM is ready
    const initialDelay = setTimeout(calculateScalingFactor, 100);
    
    // Method 1: Listen for window resize
    window.addEventListener('resize', calculateScalingFactor);
    
    // Method 2: Listen for visualViewport changes (better for mobile simulation)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', calculateScalingFactor);
    }
    
    // Method 3: Set up ResizeObserver once container is available
    let resizeObserver = setupObservers();
    
    return () => {
      clearTimeout(initialDelay);
      window.removeEventListener('resize', calculateScalingFactor);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', calculateScalingFactor);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const startSimulation = () => {
    setTime(0);
    simulationManagerWithHandicapped.setCurrentTime(0);
    simulationManagerWithoutHandicapped.setCurrentTime(0);
    setIsSimulationRunning(true);
    simulationManagerWithHandicapped.startSimulation();
    simulationManagerWithoutHandicapped.startSimulation();
  };

  const pauseSimulation = () => {
    setIsSimulationRunning(false);
    simulationManagerWithHandicapped.pauseSimulation();
    simulationManagerWithoutHandicapped.pauseSimulation();
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const toggleHandicappedMode = () => {
    setIsHandicappedMode(!isHandicappedMode);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const stats = visibleSimulationManager.getSimulationStats();
  const completedStats = visibleSimulationManager.getCompletedSummaryStats();
  const lotOccupancyPercent = visibleParkingLot.getAverageOccupancyPercentage(time, TOTAL_SIMULATION_TIME).toFixed(1);

  // Get stats from both simulations for comparison
  const handicappedStats = simulationManagerWithHandicapped.getCompletedSummaryStats();
  const nonHandicappedStats = simulationManagerWithoutHandicapped.getCompletedSummaryStats();
  
  // Calculate the "cost" of handicapped spaces (difference in times)
  const walkTimeCost = nonHandicappedStats.totalWalkTime - handicappedStats.totalWalkTime;
  const driveTimeCost = nonHandicappedStats.totalDriveTime - handicappedStats.totalDriveTime;
  
  // Format the cost times
  const formatCostTime = (seconds) => {
    if (seconds === 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
  };
  
  const walkTimeCostFormatted = formatCostTime(walkTimeCost);
  const driveTimeCostFormatted = formatCostTime(driveTimeCost);

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <div className="App" style={{ position: 'relative' }}>
            <header className="App-header">
              {/* Tiny About link in top right */}
              <Link 
                to="/about" 
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '20px',
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  fontSize: '12px',
                  fontWeight: '400',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  zIndex: 1000
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'rgba(255,255,255,1)';
                  e.target.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'rgba(255,255,255,0.7)';
                  e.target.style.background = 'transparent';
                }}
              >
                About
              </Link>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
                <h1 style={{ margin: 0 }}>Parking Lot Simulator</h1>
                <div className="time-ticker desktop" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span>
                    Time: <span>{formatTime(time)}</span> / {formatTime(TOTAL_SIMULATION_TIME)}
                  </span>
                  {/* Speed Controls */}
                  <div className="desktop" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '2px 8px' }}>
                    {[1, 4, 8, 16, 32].map((speed) => (
                      <button
                        key={speed}
                        onClick={() => setSimulationSpeed(speed)}
                        style={{
                          background: simulationSpeed === speed ? '#ffd700' : 'rgba(255,255,255,0.15)',
                          color: simulationSpeed === speed ? '#222' : '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '2px 10px',
                          fontWeight: simulationSpeed === speed ? 700 : 400,
                          fontSize: 14,
                          cursor: 'pointer',
                          outline: simulationSpeed === speed ? '2px solid #ffd700' : 'none',
                          boxShadow: simulationSpeed === speed ? '0 0 6px #ffd70088' : 'none',
                          transition: 'all 0.15s',
                        }}
                        aria-label={`Set simulation speed to ${speed}x`}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className='desktop'>Interactive parking lot simulation - ORIGINAL SCHOLARSHIP</p>
              <div className='desktop' style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                {/* Simulation Controls and Demographic Stats on the Left */}
                <div className="desktop" style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <button 
                      onClick={startSimulation} 
                      disabled={isSimulationRunning}
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                      Start Simulation
                    </button>
                    <button 
                      onClick={pauseSimulation} 
                      disabled={!isSimulationRunning}
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                      Pause
                    </button>
                    <button 
                      onClick={reloadPage}
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                      Reload
                    </button>
                  </div>
                  {/* New Controls */}
                  <div className="desktop" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    background: 'rgba(255,255,255,0.13)',
                    padding: '10px 18px',
                    borderRadius: '12px',
                    marginTop: '8px',
                    width: 'fit-content',
                    minWidth: 0,
                    alignSelf: 'flex-start',
                    boxShadow: '0 2px 8px rgba(44,62,80,0.07)'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: '12px', color: '#fff', opacity: 0.8 }}>Handicapped Walk Speed Penalty</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[
                          { value: 1.1, label: '10% slower' },
                          { value: 1.3, label: '30% slower' },
                          { value: 1.5, label: '50% slower' }
                        ].map(({ value, label }) => (
                          <button
                            key={value}
                            onClick={() => restartSimulationWithOptions(value, numCars)}
                            style={{
                              background: handicappedWalkSpeedPenalty === value ? '#ffd700' : 'rgba(255,255,255,0.15)',
                              color: handicappedWalkSpeedPenalty === value ? '#222' : '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '4px 8px',
                              fontSize: 12,
                              cursor: 'pointer',
                              outline: handicappedWalkSpeedPenalty === value ? '2px solid #ffd700' : 'none',
                              boxShadow: handicappedWalkSpeedPenalty === value ? '0 0 6px #ffd70088' : 'none',
                              transition: 'all 0.15s',
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: '12px', color: '#fff', opacity: 0.8 }}>Number of Cars</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[40, 60, 80, 100].map((value) => (
                          <button
                            key={value}
                            onClick={() => restartSimulationWithOptions(handicappedWalkSpeedPenalty, value)}
                            style={{
                              background: numCars === value ? '#ffd700' : 'rgba(255,255,255,0.15)',
                              color: numCars === value ? '#222' : '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '4px 8px',
                              fontSize: 12,
                              cursor: 'pointer',
                              outline: numCars === value ? '2px solid #ffd700' : 'none',
                              boxShadow: numCars === value ? '0 0 6px #ffd70088' : 'none',
                              transition: 'all 0.15s',
                            }}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="desktop" style={{ 
                    display: 'flex', 
                    gap: 24, 
                    fontSize: '14px',
                    color: '#fff'
                  }}>
                    <span>Cars: {stats.totalCars}</span>
                    <span>Occupied: {stats.occupiedSpaces}</span>
                    <span>Available: {stats.availableSpaces}</span>
                  </div>
                </div>
                
                {/* Handicapped Toggle Button in the Center */}
                <div className="desktop" style={{ display: 'flex', alignItems: 'center', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                  <button
                    onClick={toggleHandicappedMode}
                    style={{
                      background: isHandicappedMode ? '#ff6b6b' : '#4ecdc4',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                    }}
                  >
                    {isHandicappedMode ? (
                      <>
                        Remove
                        <span style={{ fontSize: '20px' }}>♿</span>
                      </>
                    ) : (
                      <>
                        Add
                        <span style={{ fontSize: '20px' }}>♿</span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Summary Statistics on the Right */}
                <div className="desktop" style={{
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 8, 
                  fontSize: '14px',
                  color: '#fff',
                  textAlign: 'right',
                  flex: 1
                }}>
                  {/* <div>
                    <span style={{ fontWeight: 500, color: '#fff' }}>Total Walk Time:</span> {completedStats.formatted.totalWalk}
                  </div>
                  <div>
                    <span style={{ fontWeight: 500, color: '#fff' }}>Total Drive Time:</span> {completedStats.formatted.totalDrive}
                  </div> */}
                  <div style={{ marginTop: 8, fontWeight: 600, color: '#ffd700', fontSize: 16 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 17, color: '#2e3a4d' }}>
                    🚶‍♂️ Walk & 🚗 Drive Summary:
                  </div>Lot Occupancy: {lotOccupancyPercent}%
                  </div>
                  <div style={{ 
                    marginTop: 12, 
                    padding: '8px 12px', 
                    background: 'rgba(255,255,255,0.1)', 
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: 15, color: '#ffd700', marginBottom: 4 }}>
                      ♿ Handicapped Space Cost:
                    </div>
                    <div style={{ fontSize: 13, color: '#fff' }}>
                      Walk Time: {handicappedStats.formatted.totalWalk} (♿) vs {nonHandicappedStats.formatted.totalWalk} (🚗)
                    </div>
                    <div style={{ fontSize: 13, color: '#fff' }}>
                      Drive Time: {handicappedStats.formatted.totalDrive} (♿) vs {nonHandicappedStats.formatted.totalDrive} (🚗)
                    </div>
                  </div>
                </div>
              </div>
            </header>
            
            {/* Mobile Floating Header */}
            <div className="mobile mobile-header">
              <div className="mobile-controls">
                <button 
                  onClick={toggleHandicappedMode}
                  style={{
                    background: isHandicappedMode ? '#ff6b6b' : '#4ecdc4',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {isHandicappedMode ? '- ♿' : '+ ♿' }
                </button>
                <button 
                  onClick={() => setShowMobileModal(true)}
                  className="mobile-button"
                >
                  CONTROLS
                </button>
              </div>
              <div className="mobile-stats">
                Cars: {stats.totalCars} | Occupied: {stats.occupiedSpaces}
              </div>
            </div>
            
            <main style={{ 
              display: 'flex', 
              // display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'flex-start', 
              // gap: '20px',
              // padding: '20px',
              // minHeight: 600
            }}>
              {/* Schedule Panel (left) */}
              <div className="desktop">
                <SchedulePanel 
                  scheduledCars={visibleSimulationManager.getScheduledCars()} 
                  currentTime={time}
                  formatArrivalTime={visibleSimulationManager.formatArrivalTime}
                />
              </div>
              
              <div>
                <ParkingLotComponent 
                  parkingLot={visibleParkingLot} 
                  cars={visibleSimulationManager.getCars()}
                  time={time}
                  onCarHover={setHoveredCarInfo}
                  onCarLeave={() => setHoveredCarInfo(null)}
                  scalingFactor={scalingFactor}
                />
                
                {/* Mobile Time Controls - Full Width Below Parking Lot */}
                <div className="mobile mobile-time-controls">
                  <div className="mobile-time-display">
                    <span className="mobile-time-label">Time:</span>
                    <span className="mobile-time-value">{formatTime(time)}</span>
                    <span className="mobile-time-separator">/</span>
                    <span className="mobile-time-total">{formatTime(TOTAL_SIMULATION_TIME)}</span>
                  </div>
                  <div className="mobile-speed-controls">
                    <span className="mobile-speed-label">Speed:</span>
                    {[1, 8, 16, 32].map((speed) => (
                      <button
                        key={speed}
                        onClick={() => setSimulationSpeed(speed)}
                        className={`mobile-speed-button ${simulationSpeed === speed ? 'active' : ''}`}
                        aria-label={`Set simulation speed to ${speed}x`}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Mobile Stats Row - Single Row with Both Panels */}
                <div className="mobile mobile-stats-row">
                  {/* Mobile Scheduled Cars Panel - Left Side */}
                  <div className="mobile mobile-scheduled-cars">
                    <div className="mobile-panel-header">
                      <h3 className="mobile-panel-title">📅 Scheduled ({visibleSimulationManager.getScheduledCars().length})</h3>
                      <button 
                        className="mobile-view-all-button"
                        onClick={() => setShowScheduledModal(true)}
                      >
                        View All
                      </button>
                    </div>
                  </div>
                  
                  {/* Mobile Completed Cars Panel - Right Side */}
                  <div className="mobile mobile-completed-cars">
                    <div className="mobile-panel-header">
                      <h3 className="mobile-panel-title">✅ Exited ({visibleSimulationManager.getCompletedCars().length})</h3>
                      <button 
                        className="mobile-view-all-button"
                        onClick={() => setShowCompletedModal(true)}
                      >
                        View All
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Mobile Summary Statistics */}
                <div className="mobile mobile-summary-stats">
                  <div style={{ 
                    background: 'rgba(44, 62, 80, 0.9)', 
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: '16px',
                    margin: '12px 0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '17px', color: '#ffffff', marginBottom: '12px' }}>
                      🚶‍♂️ Walk & 🚗 Drive Summary:
                    </div>
                    <div style={{ fontSize: '15px', color: '#ffffff', marginBottom: '16px' }}>
                      Lot Occupancy: {lotOccupancyPercent}%
                    </div>
                    <div style={{ 
                      padding: '8px 12px', 
                      background: 'rgba(255,255,255,0.1)', 
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#ffd700', marginBottom: '8px' }}>
                        ♿ Handicapped Space Cost:
                      </div>
                      <div style={{ fontSize: '13px', color: '#ffffff', marginBottom: '4px' }}>
                        Walk Time: {handicappedStats.formatted.totalWalk} (♿) vs {nonHandicappedStats.formatted.totalWalk} (🚗)
                      </div>
                      <div style={{ fontSize: '13px', color: '#ffffff' }}>
                        Drive Time: {handicappedStats.formatted.totalDrive} (♿) vs {nonHandicappedStats.formatted.totalDrive} (🚗)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Completed Cars Panel (right) */}
              <div className="desktop">
                <CompletedCarsPanel 
                  completedCars={visibleSimulationManager.getCompletedCars()} 
                  formatTime={formatTime}
                  formatArrivalTime={visibleSimulationManager.formatArrivalTime}
                />
              </div>
                      
              {/* Optionally, show hovered car info as a floating tooltip */}
              {hoveredCarInfo && (
                <div style={{
                  position: 'fixed',
                  left: '50%',
                  top: '10%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#212529',
                  color: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  zIndex: 10000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Car {hoveredCarInfo.unicodeChar}
                  </div>
                  <div>ID: {hoveredCarInfo.id}</div>
                  <div>Status: {hoveredCarInfo.status}</div>
                  <div>Arrival: {visibleSimulationManager.formatArrivalTime(hoveredCarInfo.arrivalTime)}</div>
                  {hoveredCarInfo.person && (
                    <>
                      <div style={{ marginTop: '8px', fontWeight: 'bold' }}>
                        Person Info:
                      </div>
                      <div>Walk Speed: {hoveredCarInfo.person.walkSpeed} px/s</div>
                      <div>Lot Speed: {hoveredCarInfo.person.lotSpeed} px/s</div>
                      <div>Store Visit: {Math.floor(hoveredCarInfo.person.storeVisitTime / 60)}m {hoveredCarInfo.person.storeVisitTime % 60}s</div>
                      <div>Walk Time: {hoveredCarInfo.person.accumulatedWalkTimeFormatted}</div>
                      <div>Driving Time: {hoveredCarInfo.person.drivingTimeFormatted}</div>
                      <div>Handicapped: {hoveredCarInfo.person.handicapped ? 'Yes' : 'No'}</div>
                    </>
                  )}
                </div>
              )}
            </main>
            
            {/* Mobile Controls Modal */}
            {showMobileModal && (
              <div className="mobile-modal" onClick={() => setShowMobileModal(false)}>
                <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="mobile-modal-header">
                    <h3 className="mobile-modal-title">Simulation Controls</h3>
                    <button 
                      className="mobile-modal-close"
                      onClick={() => setShowMobileModal(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ color: '#333', lineHeight: '1.6' }}>
                    {/* Simulation Control Buttons */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                        <button 
                          onClick={startSimulation} 
                          disabled={isSimulationRunning}
                          style={{ 
                            padding: '8px 16px', 
                            fontSize: '14px',
                            background: isSimulationRunning ? '#ccc' : '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isSimulationRunning ? 'not-allowed' : 'pointer',
                            flex: 1
                          }}
                        >
                          Start Simulation
                        </button>
                        <button 
                          onClick={pauseSimulation} 
                          disabled={!isSimulationRunning}
                          style={{ 
                            padding: '8px 16px', 
                            fontSize: '14px',
                            background: !isSimulationRunning ? '#ccc' : '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: !isSimulationRunning ? 'not-allowed' : 'pointer',
                            flex: 1
                          }}
                        >
                          Pause
                        </button>
                        <button 
                          onClick={reloadPage}
                          style={{ 
                            padding: '8px 16px', 
                            fontSize: '14px',
                            background: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            flex: 1
                          }}
                        >
                          Reload
                        </button>
                      </div>
                    </div>

                    {/* Handicapped Walk Speed Penalty */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: 'bold', 
                        marginBottom: '8px',
                        color: '#333'
                      }}>
                        Handicapped Walk Speed Penalty
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[
                          { value: 1.1, label: '10% slower' },
                          { value: 1.3, label: '30% slower' },
                          { value: 1.5, label: '50% slower' }
                        ].map(({ value, label }) => (
                          <button
                            key={value}
                            onClick={() => restartSimulationWithOptions(value, numCars)}
                            style={{
                              background: handicappedWalkSpeedPenalty === value ? '#ffd700' : '#f0f0f0',
                              color: handicappedWalkSpeedPenalty === value ? '#222' : '#333',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              flex: 1,
                              fontWeight: handicappedWalkSpeedPenalty === value ? 'bold' : 'normal'
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Number of Cars */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: 'bold', 
                        marginBottom: '8px',
                        color: '#333'
                      }}>
                        Number of Cars
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[40, 60, 80, 100].map((value) => (
                          <button
                            key={value}
                            onClick={() => restartSimulationWithOptions(handicappedWalkSpeedPenalty, value)}
                            style={{
                              background: numCars === value ? '#ffd700' : '#f0f0f0',
                              color: numCars === value ? '#222' : '#333',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              flex: 1,
                              fontWeight: numCars === value ? 'bold' : 'normal'
                            }}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Mobile Scheduled Cars Modal */}
            {showScheduledModal && (
              <div className="mobile-modal" onClick={() => setShowScheduledModal(false)}>
                <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="mobile-modal-header">
                    <h3 className="mobile-modal-title">📅 All Scheduled Cars ({visibleSimulationManager.getScheduledCars().length})</h3>
                    <button 
                      className="mobile-modal-close"
                      onClick={() => setShowScheduledModal(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="mobile-modal-cars-list">
                    {visibleSimulationManager.getScheduledCars().map(({ car, person }, index) => {
                      const arrivalTime = car.getArrivalTime();
                      const isPastTime = arrivalTime <= time;
                      
                      return (
                        <details key={car.id} className={`mobile-modal-car-item ${isPastTime ? 'past-time' : ''}`}>
                          <summary 
                            className="mobile-modal-car-header"
                            onClick={(e) => e.stopPropagation()}
                            style={{width: '100%'}}
                          >
                            <span className="mobile-modal-car-unicode">
                              {car.getUnicodeChar()}
                            </span>
                            <div className="mobile-modal-car-details">
                              <div className="mobile-modal-car-time">
                                Arrival: {visibleSimulationManager.formatArrivalTime(arrivalTime)}
                              </div>
                              <div className="mobile-modal-car-number">
                                Car #{index + 1}
                              </div>
                              <div className="mobile-modal-car-status">
                                Status: {isPastTime ? 'Arrived' : 'Scheduled'}
                              </div>
                            </div>
                          </summary>
                          
                          <div className="mobile-modal-car-expanded">
                            <div className="mobile-modal-car-info-section">
                              <div className="mobile-modal-car-info-title">Car Details:</div>
                              <div>ID: {car.id}</div>
                              <div>Arrival: {visibleSimulationManager.formatArrivalTime(arrivalTime)}</div>
                              <div>Status: {isPastTime ? 'Arrived' : 'Scheduled'}</div>
                            </div>
                            
                            {person && (
                              <div className="mobile-modal-car-info-section">
                                <div className="mobile-modal-car-info-title">Person Info:</div>
                                <div>Walk Speed: {person.walkSpeed} px/s</div>
                                <div>Lot Speed: {person.lotSpeed} px/s</div>
                                <div>Store Visit: {Math.floor(person.storeVisitTime / 60)}m {person.storeVisitTime % 60}s</div>
                                <div>Handicapped: {person.handicapped ? 'Yes' : 'No'}</div>
                              </div>
                            )}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Mobile Completed Cars Modal */}
            {showCompletedModal && (
              <div className="mobile-modal" onClick={() => setShowCompletedModal(false)}>
                <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="mobile-modal-header">
                    <h3 className="mobile-modal-title">✅ All Exited Cars ({visibleSimulationManager.getCompletedCars().length})</h3>
                    <button 
                      className="mobile-modal-close"
                      onClick={() => setShowCompletedModal(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="mobile-modal-cars-list">
                    {visibleSimulationManager.getCompletedCars().map(({ car, person }, index) => {
                      const carInfo = car.getCarInfo();
                      
                      return (
                        <details key={car.id} className="mobile-modal-car-item completed">
                          <summary 
                            className="mobile-modal-car-header"
                            onClick={(e) => e.stopPropagation()}
                            style={{width: '100%'}}
                          >
                            <span className="mobile-modal-car-unicode">
                              {car.getUnicodeChar()}
                            </span>
                            <div className="mobile-modal-car-details">
                              <div className="mobile-modal-car-time">
                                Arrived: {visibleSimulationManager.formatArrivalTime(car.getArrivalTime())}
                              </div>
                              <div className="mobile-modal-car-duration">
                                Duration: {carInfo.exitTime && carInfo.actualArrivalTime ? 
                                  formatTime(Math.floor((carInfo.exitTime - carInfo.actualArrivalTime) / 1000)) : 'N/A'}
                              </div>
                              <div className="mobile-modal-car-number">
                                Car #{index + 1}
                              </div>
                            </div>
                          </summary>
                          
                          <div className="mobile-modal-car-expanded">
                            <div className="mobile-modal-car-info-section">
                              <div className="mobile-modal-car-info-title">Car Details:</div>
                              <div>ID: {car.id}</div>
                              <div>Scheduled: {visibleSimulationManager.formatArrivalTime(car.getArrivalTime())}</div>
                              <div>Actual Arrival: {carInfo.actualArrivalTime ? formatTime(carInfo.actualArrivalTime) : 'N/A'}</div>
                              <div>Exit Time: {carInfo.exitTime ? formatTime(carInfo.exitTime) : 'N/A'}</div>
                              <div>Total Duration: {carInfo.exitTime && carInfo.actualArrivalTime ? 
                                formatTime(Math.floor((carInfo.exitTime - carInfo.actualArrivalTime) / 1000)) : 'N/A'}</div>
                            </div>
                            
                            {person && (
                              <div className="mobile-modal-car-info-section">
                                <div className="mobile-modal-car-info-title">Person Stats:</div>
                                <div>Walk Speed: {person.walkSpeed} px/s</div>
                                <div>Lot Speed: {person.lotSpeed} px/s</div>
                                <div>Store Visit: {Math.floor(person.storeVisitTime / 60)}m {person.storeVisitTime % 60}s</div>
                                <div>Total Walk Time: {person.accumulatedWalkTimeFormatted}</div>
                                <div>Total Driving Time: {person.drivingTimeFormatted}</div>
                                <div>Handicapped: {person.handicapped ? 'Yes' : 'No'}</div>
                              </div>
                            )}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        } />
        <Route path="/about" element={<About />} />
      </Routes>
    </Router>
  );
}

export default App;
